import React, { ReactElement } from "react";
import { AutoSizer } from "react-virtualized";
import clsx from "clsx";

import AppPageTable from "components/AppPageTable";
import AppPageTableHeader from "components/AppPageTableHeader";
import AppPageTableContent from "components/AppPageTableContent";
import TrainerAccordionView from "components/VetExamsTable/TrainerAccordionView";
import LockableRow from "components/VetExamsTable/LockableRow";
import Loader from "components/Loader";
import ScrollSync from "components/ScrollSync";
import ErrorBoundary from "components/ErrorBoundary";
import { IExamsTableRowData } from "./types";
import { NO_TRAINER_TITLE } from "./helper";

import useStyles from "./styles";

interface IProps {
  rows: IExamsTableRowData[];
  isFetchingNextPage: boolean;
  isRefetching?: boolean;
  isMappedByTrainer: boolean;
  tableHeaderContent: ReactElement;
  noResultsContent: ReactElement;
  tableBodyRef: React.RefObject<HTMLDivElement>;
  renderRow: (rowData: IExamsTableRowData, index: number) => React.ReactElement;
  refetchData?: (value?: boolean) => Promise<void>;
  hasTrainerAccordionViewFixedCells?: boolean;
  shouldUseLockableRow?: boolean;
}

type TTrainerEntries = Record<string, IExamsTableRowData[]>;

const VetExamsTable = (props: IProps) => {
  const {
    rows,
    isMappedByTrainer,
    noResultsContent,
    tableHeaderContent,
    isFetchingNextPage,
    isRefetching = false,
    tableBodyRef,
    refetchData,
    renderRow,
    hasTrainerAccordionViewFixedCells = false,
    shouldUseLockableRow = false
  } = props;

  const classes = useStyles();

  const handleExamUnlock = async () => {
    await refetchData();
  };

  const renderLockableRow = (row: IExamsTableRowData, index: number) => {
    const { examId, eventId } = row;

    return (
      <LockableRow
        key={examId || eventId}
        recordId={examId}
        onUnlock={handleExamUnlock}
      >
        {renderRow(row, index)}
      </LockableRow>
    );
  };

  const renderPlainRow = (row: IExamsTableRowData, index: number) => {
    const { examId, eventId } = row;

    return <div key={examId || eventId}>{renderRow(row, index)}</div>;
  };

  const renderStandardView = (data: IExamsTableRowData[]) => {
    const renderRowCallback = shouldUseLockableRow
      ? renderLockableRow
      : renderPlainRow;

    return data.map(renderRowCallback);
  };

  const renderTrainerAccordionsView = (data: IExamsTableRowData[]) => {
    const mappedRowsByTrainer = data.reduce((acc, cur) => {
      const key = cur.horse.trainer?.id ?? NO_TRAINER_TITLE;

      acc[key] = acc[key] ? [...acc[key], cur] : [cur];

      return acc;
    }, {} as TTrainerEntries);

    const mappedRowEntries = Object.entries(mappedRowsByTrainer);

    const noTrainerItemIndex = mappedRowEntries.findIndex(
      ([key]) => key === NO_TRAINER_TITLE
    );

    let sortedRowsWithNoTrainerFirst = mappedRowEntries;

    if (noTrainerItemIndex !== -1) {
      const extracted = mappedRowEntries.splice(noTrainerItemIndex, 1)[0];
      sortedRowsWithNoTrainerFirst = [extracted, ...mappedRowEntries];
    }

    return (
      <div>
        {sortedRowsWithNoTrainerFirst.map(([trainerId, rows]) => {
          const trainerName =
            rows[0]?.horse.trainer?.lastName?.toUpperCase() ?? NO_TRAINER_TITLE;

          return (
            <TrainerAccordionView
              key={trainerId}
              entries={rows}
              trainerName={trainerName}
              renderRows={renderStandardView}
              hasFixedHeaderCells={hasTrainerAccordionViewFixedCells}
            />
          );
        })}
      </div>
    );
  };

  const renderTableContent = () => {
    if (!rows.length) {
      return noResultsContent;
    }

    return (
      <>
        <ScrollSync enabled={true}>
          <ErrorBoundary>
            <AppPageTableHeader className={classes.appPageTableHeader}>
              {tableHeaderContent}
            </AppPageTableHeader>
          </ErrorBoundary>

          <ErrorBoundary>
            <AppPageTableContent className={classes.appPageTableContent}>
              <AutoSizer>
                {({ height, width }) => (
                  <div
                    className={classes.scrollContainer}
                    style={{ height: height, width: width }}
                    ref={tableBodyRef}
                  >
                    {isMappedByTrainer
                      ? renderTrainerAccordionsView(rows)
                      : renderStandardView(rows)}
                  </div>
                )}
              </AutoSizer>

              {isRefetching && (
                <Loader overlay className={classes.refetchingOverlay} />
              )}
            </AppPageTableContent>
          </ErrorBoundary>
        </ScrollSync>

        {isFetchingNextPage && (
          <Loader
            className={clsx(classes.infiniteLoader, classes.showInfiniteLoader)}
          />
        )}
      </>
    );
  };

  return (
    <AppPageTable className={classes.appPageTable}>
      {renderTableContent()}
    </AppPageTable>
  );
};

export default React.memo(VetExamsTable);
